import { api } from '../api/client';

import { API_ITEM_TAG_REASONS_URL, API_TAG_ITEMS_URL, API_TAG_MERGE_URL, API_TAG_RESTORE_URL, API_TAG_RULE_PREVIEW_URL, API_TAG_RULE_URL, API_TAG_URL, API_TAGS_SEARCH_URL, API_TAGS_URL, API_TAGS_USABLE_URL } from '../utils';
import { disableAutomation, enableAutomation } from './automations';
import { withElementChoicesField } from '../utils/elementRefusal';

/**
 * The rebuilt tags (docs/plans/tags.md §3.8, T2a; keepr-api docs/SCHEMA.md §
 * Tags): the vocabulary, and creating, changing, deleting and restoring a
 * kind-'tag' tag. Two owners: a PRIVATE tag (`owner: 'me'`, one person's,
 * never in History) and a COLLECTION tag (`collection_id`, written by the
 * collection's managers, its rows in the collection's History).
 *
 * Refusals keep the API's own sentence (utils/tags.js tagRefusal reads it);
 * the codes a surface acts on — `tag_name_taken`, `tag_limit_reached`,
 * `tag_has_children`, `tag_in_use` (+ `reason`, `references`),
 * `invalid_parent`, `collection_archived` — ride `error.response.data`.
 *
 * Item tags (kind 'item', T2c) are never written here: they follow their
 * items and their card's setting (services/itemTags.js, T4d). The rule
 * (T6a) arrives as its own calls; the impact report before a delete is
 * services/tagReferences.js (ruled d33/d38), and its choices ride
 * `deleteTag`'s `cleanup`. `mergeTag` (T9) folds one tag into another;
 * `getUsableTags` (T9) says which of a template's tags land for the caller.
 * `searchTags` (T3's `GET /api/tags/search`) is how a picker finds item tags:
 * the vocabulary never lists them (plan §3.2), only a count per card
 * (`getCollectionVocabulary`).
 */

const tagsOf = data => (Array.isArray(data?.tags) ? data.tags : []);

/** My private tags, in tree order (`status: 'deleted'`: the ones I may restore). */
export async function getMyTags({ status } = {}) {
	const params = { owner: 'me', ...(status === 'deleted' ? { status } : {}) };
	const { data } = await api.get(API_TAGS_URL, { params });
	return tagsOf(data);
}

/**
 * A collection's vocabulary: its own tags, then each ancestor's the caller
 * may read (a sub-collection's items may carry their parent's), each owner's
 * in tree order with `depth` and `path`. Restricted tags only where the caller
 * reads their owner of their own (ruled d24). An automatic tag's `rule`
 * carries `enabled` (false while its rule is paused — keepr-api T6b, derived
 * from the rule's automation). `status: 'deleted'` — the
 * collection's OWN deleted tags, managers only (403 otherwise).
 */
export async function getCollectionTags(collectionId, { status } = {}) {
	const params = { collection_id: collectionId, ...(status === 'deleted' ? { status } : {}) };
	const { data } = await api.get(API_TAGS_URL, { params });
	return tagsOf(data);
}

/**
 * The same vocabulary with its item-tag rows (T4d): `{ tags, itemTagCards }`.
 * The vocabulary never lists item tags one by one (plan §3.2): each card that
 * uses its items as tags — in the collection or an ancestor the caller reads
 * — is one row, `{ collection_id, card_id, mode, applyWhereReferenced, count }`,
 * `count` the item tags whose items the CALLER can read. One request serves
 * both (hooks/useCollectionTags.js).
 */
export async function getCollectionVocabulary(collectionId) {
	const { data } = await api.get(API_TAGS_URL, { params: { collection_id: collectionId } });
	return {
		tags: tagsOf(data),
		itemTagCards: Array.isArray(data?.itemTagCards) ? data.itemTagCards : [],
	};
}

/**
 * The fields a tag write may carry; anything else is the API's 400
 * `unknown_key`. `applyToNewItemsOf` (T7, ruled d22): a restricted tag's
 * cards — its new items get the tag as they are made; a manager's act from
 * keepr itself (a key is 403 `session_required`), at most 20 cards of the
 * tag's collection or a sub-collection (else 400 `invalid_tag`, `field:
 * 'applyToNewItemsOf'`), only on a restricted tag; un-restricting clears it.
 */
const FIELDS = ['name', 'description', 'color', 'icon', 'aliases', 'parentId', 'position', 'restricted', 'applyToNewItemsOf'];
const pick = fields => Object.fromEntries(FIELDS.filter(key => fields && fields[key] !== undefined).map(key => [key, fields[key]]));

/**
 * Creates a tag. `owner` is `'me'` (a private tag) or `{ collectionId }` (a
 * collection tag; manage on it). → the tag (201 `{ payload }`).
 */
export async function createTag(owner, fields = {}) {
	const body = owner === 'me'
		? { owner: 'me', ...pick(fields) }
		: { collection_id: owner?.collectionId, ...pick(fields) };
	const { data } = await api.post(API_TAGS_URL, body);
	return data?.payload || null;
}

/**
 * Changes a tag. Send only what changed: the API judges only what changes
 * (a field sent back as stored is an echo and saves), and a tag's owner
 * never changes. → the tag.
 */
export async function updateTag(id, patch = {}) {
	const { data } = await api.patch(API_TAG_URL(id), pick(patch));
	return data?.payload || null;
}

/**
 * The same change, asked and not made (`PATCH … { dryRun: true }`, keepr-api
 * b75): every refusal the save would meet, and nothing written. → `{ tag,
 * warnings }` — `tag` as it would be; `warnings` the API's, [] when none.
 * The one warning today is `shows_new_items`: "Apply to new items of" gains a
 * card, and access settings that show items with the tag will show those new
 * items too (`references` the ones the caller manages, `hidden` the rest,
 * `cardIds` the cards added). The tag dialog asks it before such a save.
 */
export async function checkTagUpdate(id, patch = {}) {
	const { data } = await api.patch(API_TAG_URL(id), { ...pick(patch), dryRun: true });
	return { tag: data?.payload || null, warnings: Array.isArray(data?.warnings) ? data.warnings : [] };
}

/**
 * Deletes a tag — soft, and restorable. A collection tag comes off every
 * item that carries it (`applications: { items, hand, automatic, pulled }`);
 * a private tag's own uses go (`applications: { removed }`).
 *
 * `cleanup` (T4d) — the impact report's "Remove for me" choices,
 * `[{ holder, id }]` (services/tagReferences.js), applied after the delete:
 * the answer then carries `cleanup: [{ holder, id, outcome }]`. Without one
 * the DELETE has no body, as before.
 */
export async function deleteTag(id, { cleanup } = {}) {
	const { data } = Array.isArray(cleanup) && cleanup.length
		? await api.delete(API_TAG_URL(id), { data: { cleanup } })
		: await api.delete(API_TAG_URL(id));
	return data || { ok: true };
}

/**
 * Brings a deleted tag back where it was: a collection tag goes back on the
 * items its newest delete took it off (`applications: { restored, skipped }`);
 * a private tag comes back bare. → `{ tag, applications }`.
 */
export async function restoreTag(id) {
	const { data } = await api.post(API_TAG_RESTORE_URL(id));
	return { tag: data?.payload || null, applications: data?.applications || null };
}

/**
 * MERGE (T9, keepr-api `POST /api/tags/{id}/merge`, docs/SCHEMA.md § Merging
 * tags): folds tag `id` into `into`, another tag of the same owner. Its items,
 * the tags under it, its name (as an alias, where there is room), the filters
 * and rules that name it, templates and pins all go to `into`, and it is gone
 * for good. `dryRun` asks every question and writes nothing — the dialog's
 * counts. → `{ tag (the survivor, null on a dry run), merge }` where `merge`
 * is `{ from, into, items, hand, automatic, private?, children, aliases:
 * { added, dropped }, nameKept, queries: { rewritten, notRewritten,
 * truncated? }, templates?, pins?, skipped?, childrenLeft? }`.
 */
export async function mergeTag(id, into, { dryRun = false } = {}) {
	const { data } = await api.post(API_TAG_MERGE_URL(id), { into, ...(dryRun ? { dryRun: true } : {}) });
	return { tag: data?.payload || null, merge: data?.merge || null };
}

/**
 * WHICH OF THESE TAGS LAND HERE (T9, keepr-api `GET /api/tags/usable`): each
 * id judged for the caller in the collection a new item would be made in —
 * `{ tagIds, privateTagIds, dropped: [{ tagId, reason, name? }] }`, `reason`
 * one of `restricted`, `rule`, `deleted`, `unavailable` (no name). A draft, a
 * plain duplicate and a multi add step ask it; a quick add's resolve answers
 * the same shape itself. No ids: nothing to ask.
 */
export async function getUsableTags(collectionId, ids = []) {
	const list = [...new Set((ids || []).filter(Boolean).map(id => `${id}`.toLowerCase()))].slice(0, 50);
	if (!collectionId || !list.length) return { tagIds: [], privateTagIds: [], dropped: [] };
	const { data } = await api.get(API_TAGS_USABLE_URL, { params: { collection_id: collectionId, ids: list.join(',') } });
	return {
		tagIds: Array.isArray(data?.tagIds) ? data.tagIds : [],
		privateTagIds: Array.isArray(data?.privateTagIds) ? data.privateTagIds : [],
		dropped: Array.isArray(data?.dropped) ? data.dropped : [],
	};
}

/** The page size tag search answers at most (keepr-api tagPage LIMITS.searchMaxPage). */
export const TAG_SEARCH_LIMIT = 50;
/** The longest query tag search takes (LIMITS.searchQuery). */
export const TAG_SEARCH_MAX_LENGTH = 60;

/**
 * `GET /api/tags/search?q=` — the caller's private tags whose name or an
 * alias contains `q`, the collection tags they can read (with `path`), and
 * item tags whose ITEM's title contains `q`, read through their own scope,
 * in that order: `{ results: [{ kind: 'private' | 'collection' | 'item',
 * tag, collection? }], more }`. An item tag's `tag` carries its item's
 * `title` and `card_id`; `collection` is `{ _id, name? }` — the collection
 * the tag belongs to (an item tag's: its item's). `collectionId` narrows to
 * that collection's vocabulary (and its ancestors') and item tags of its
 * family. `cardId` answers ITEM tags alone, those whose item is of that card
 * or a card extending it (Settings › Tags' search on one card's row; keepr-api
 * `card_id`, the T4d follow-up). At most 50 per kind; `q` is 1–60 characters.
 */
export async function searchTags(q, { collectionId = null, cardId = null, limit = TAG_SEARCH_LIMIT, skip = 0, signal } = {}) {
	const text = `${q || ''}`.replace(/\s+/g, ' ').trim().slice(0, TAG_SEARCH_MAX_LENGTH);
	if (!text) return { results: [], more: false };
	const params = {
		q: text,
		limit,
		...(skip ? { skip } : {}),
		...(collectionId ? { collection_id: collectionId } : {}),
		...(cardId ? { card_id: cardId } : {}),
	};
	const { data } = await api.get(API_TAGS_SEARCH_URL, { params, signal });
	return {
		results: Array.isArray(data?.results) ? data.results : [],
		more: Boolean(data?.more),
	};
}

/** How many rows the tag page asks for at once (keepr-api queryContract's default page is larger; a page of tiles is plenty). */
export const TAG_PAGE_SIZE = 60;

/**
 * THE TAG PAGE's read (T5 over T3's `GET /api/tags/{id}/items`, keepr-api
 * docs/SCHEMA.md § The tag page and tag search) — any kind of tag:
 *
 *   { tag, total, items, cards: [{ card_id, count }],
 *     collections: [{ collection_id, name?, count }], children?: [{ tag, count }] }
 *
 * `items` are the rows the caller's own `?q=tags=<id>` means (the tag and every
 * tag under it, their private applications included), each with `tagTitles`,
 * `myTags` and `collection: { _id, name? }`; `children` comes with the first
 * page of a kind-'tag' tag only. `cardId` narrows to a card and its descendant
 * cards, `collectionId` to a collection and its sub-collections; `cards` and
 * `collections` always count the whole page (the choices stay whole).
 *
 * A tag the caller cannot open answers 404 exactly as a missing one does — the
 * caller never learns which — and the page treats every 404 (and a malformed
 * id's 400) the same way.
 */
export async function getTagItems(tagId, { cardId = null, collectionId = null, limit = TAG_PAGE_SIZE, skip = 0, signal } = {}) {
	const params = {
		limit,
		...(skip ? { skip } : {}),
		...(cardId ? { card_id: cardId } : {}),
		...(collectionId ? { collection_id: collectionId } : {}),
	};
	const { data } = await api.get(API_TAG_ITEMS_URL(tagId), { params, signal });
	return {
		tag: data?.tag || null,
		total: Number(data?.total) || 0,
		items: Array.isArray(data?.items) ? data.items : [],
		cards: Array.isArray(data?.cards) ? data.cards : [],
		collections: Array.isArray(data?.collections) ? data.collections : [],
		children: Array.isArray(data?.children) ? data.children : null,
		cardsTruncated: Boolean(data?.cardsTruncated),
		childrenTruncated: Boolean(data?.childrenTruncated),
	};
}

// ------------------------------------------------------------------ automatic tags (T6a)
//
// An AUTOMATIC TAG is a collection tag with a rule (docs/plans/tags.md §3.4;
// keepr-api docs/SCHEMA.md § Automatic tags). The rule is authored on the tag
// and runs as a managed automation; the tag carries `rule: { automation_id,
// strict }`. Managers of the tag's collection only — a reader asks why an
// item carries one (`getTagReasons`) instead.
//
// The rule on the wire:
//   { card_id, where?, match: 'all' | 'any',
//     related: [{ card_id, link, filter?, window?: { field, unit, every },
//                 aggregate, of?, op, value, whenNone }],   // 0–3
//     strict }
//
// A pass that makes a rule true (sync), pauses it or stops it runs on the
// server, its first slice in the request; `pass` is `{ _id, kind, status,
// running, progress, … }` and `getTagRule` answers the latest one.

/**
 * `GET /api/tags/{id}/rule` → `{ rule, enabled, timeDependent, daily, pass }`
 * (`rule` null for a hand tag). `daily` (keepr-api T6b) is a time-dependent
 * rule's once-a-day re-check — `{ at: 'HH:MM', timezone: 'UTC', nextRunAt }`,
 * a time the server chose — else null.
 */
export async function getTagRule(tagId) {
	const { data } = await api.get(API_TAG_RULE_URL(tagId));
	return {
		rule: data?.rule || null,
		enabled: Boolean(data?.enabled),
		timeDependent: Boolean(data?.timeDependent),
		daily: data?.daily && typeof data.daily === 'object' ? data.daily : null,
		pass: data?.pass || null,
	};
}

/**
 * `POST /api/tags/{id}/rule/preview { rule }` — how many items the rule
 * would tag, nothing written: `{ total, matching, scanned, complete,
 * applied, sample: [{ item_id, title, collection_id, card_id }], removesHand?,
 * timeDependent }` — each sample names its own collection and card (T6b).
 * A rule with related clauses is evaluated over the first 200 items of its
 * card (`complete: false` past that).
 */
export async function previewTagRule(tagId, rule, { elementChoices = null } = {}) {
	// The person's answer to an ambiguous element name in the rule (utils/elementRefusal.js).
	const { data } = await api.post(API_TAG_RULE_PREVIEW_URL(tagId), withElementChoicesField({ rule }, elementChoices));
	return data || null;
}

/**
 * `PUT /api/tags/{id}/rule { rule, dryRun?, confirm? }` — gives the tag a
 * rule, reshapes it, or switches strict. `dryRun` answers `{ dryRun, change,
 * preview?, removesHand?, confirm?, timeDependent }` and writes nothing; the
 * real call answers `{ payload: Tag, rule, enabled, timeDependent, daily,
 * pass }` (`payload.rule.enabled`, T6b). Turning strict on with hand
 * applications must echo the dry run's `confirm` (else 409
 * `confirm_required`, with the count). Rate class `tagRule`: 20 saves an
 * hour — a dry run, a refused save and a save that changes nothing are not
 * counted (T6b).
 */
export async function putTagRule(tagId, rule, { dryRun = false, confirm, elementChoices = null } = {}) {
	const body = { rule, ...(dryRun ? { dryRun: true } : {}), ...(confirm ? { confirm } : {}) };
	const { data } = await api.put(API_TAG_RULE_URL(tagId), withElementChoicesField(body, elementChoices));
	return data || null;
}

/**
 * "Stop applying by rule" — `PUT … { rule: null }`. `dryRun` answers
 * `{ dryRun, change: 'stop', movesToHand, full, fullIds }` — `full` the count
 * of items that already carry 50 hand tags, `fullIds` the first 20 of them
 * the caller can read (T6b); the real call answers the tag (`payload`, now a
 * hand tag) and the stop `pass`, whose `progress.full` lists them again.
 */
export async function stopTagRule(tagId, { dryRun = false } = {}) {
	const { data } = await api.put(API_TAG_RULE_URL(tagId), { rule: null, ...(dryRun ? { dryRun: true } : {}) });
	return data || null;
}

/**
 * Pause (`enabled` false) or resume an automatic tag's rule: the enable /
 * disable switch every automation has, keyed on the tag's
 * `rule.automation_id`. Pausing takes the rule's automatic applications off
 * (hand ones stay); resuming puts back what holds. → `{ enabled, pass }`.
 */
export async function setTagRuleEnabled(collectionId, automationId, enabled) {
	const response = await (enabled ? enableAutomation : disableAutomation)(collectionId, automationId);
	const data = response?.data || {};
	return { enabled: data.enabled !== false, pass: data.pass || null };
}

/**
 * `GET /api/items/{id}/tag-reasons` — why each automatic tag on the item
 * holds: `[{ tagId, since, facts: { match, where, related: [{ i, card_id,
 * aggregate, of?, op, value, holds, actual, none?, item?: { item_id, title,
 * collection_id, card_id } }] } }]`. A related item is named only when the
 * reader can read it, with its own collection and card (T6b).
 */
export async function getTagReasons(itemId) {
	const { data } = await api.get(API_ITEM_TAG_REASONS_URL(itemId));
	return Array.isArray(data?.reasons) ? data.reasons : [];
}
